<template>
  <div>
    <div
      v-if="isLoading"
      :class="$style.loading"
    >
      <LoadingIcon
        :scale="2"
      />
    </div>
    <div
      v-else
      :class="$style.contentContainer"
      data-test-id="CheckoutFlow__contentContainer"
    >
      <div
        v-for="(section, index) in sections"
        :key="section.id"
        :class="$style.sectionContainer"
      >
        <hr
          v-if="index !== 0"
          :class="$style.horizontalLine"
        >
        <component
          :is="section.component"
          v-bind="section.props"
          :data-test-id="$testId(section.component)"
          :hasReachedSection="index < farthestStepReached"
          :isEditMode="step === section.id"
          :location="location"
          :stepNumber="index + 1"
          @cancelled="handleContinue"
          @clickedContinue="section.handleContinue"
          @clickedEdit="handleEdit"
          @error="handleError"
        />
      </div>
      <hr>
      <div
        v-if="isFinalReviewStep && areRequiredPaymentFieldsSet"
        :class="$style.buttonContainer"
      >
        <Button
          :class="$style.primaryButton"
          data-test-id="CheckoutFlow__submitButton"
          :isLoading="primaryButtonIsLoading"
          :text="primaryButtonText"
          @click="handleSubmit"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  Button,
  LoadingIcon,
} from '@jumpcloud/ui-components';
import {
  PaymentModel,
  paymentModuleName,
} from '@jumpcloud/ui-payments';
import { eventProperties } from 'client/GrowthEngineering/utilities/GrowthConstants';
import { get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import { paymentFormSections } from 'client/Payments/constants/PaymentConstants';
import AddOnFeaturesSection from 'client/Payments/newComponents/AddOnFeaturesSection.vue';
import BillingInformationSection from 'client/Payments/newComponents/BillingInformationSection.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import PaymentMethodSection from 'client/Payments/newComponents/PaymentMethodSection.vue';
import SubscriptionConstants from 'client/Payments/pricingMatrix/SubscriptionConstants';
import SubscriptionTypeSection from 'client/Payments/newComponents/SubscriptionTypeSection.vue';
import loadVuexModule from 'client/store/loadVuexModule';

const pricingModule = 'PricingModule';
const orgModel = 'OrganizationModel';
const settingsModule = 'SettingsModule';
const planModule = 'PlanModule';

const {
  description,
  location,
  section,
  text: textEventProp,
} = eventProperties;

const {
  addOnFeaturesSection,
  billingInformationSection,
  finalReview,
  paymentMethodSection,
  subscriptionTypeSection,
} = paymentFormSections;

const {
  getProductCodeByType,
  SubscriptionTypes,
} = SubscriptionConstants;

export default {
  name: 'CheckoutFlow',

  EventHelper,
  addOnFeaturesSection,
  paymentMethodSection,

  components: {
    AddOnFeaturesSection,
    BillingInformationSection,
    Button,
    LoadingIcon,
    PaymentMethodSection,
    SubscriptionTypeSection,
  },

  props: {
    components: {
      type: Array,
      default: undefined,
    },
    id: {
      type: String,
      required: true,
    },
    location: {
      type: String,
      required: true,
    },
    productCode: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      farthestStepReached: 0,
      isBillingEdited: false,
      isLoading: true,
      primaryButtonIsLoading: false,
      primaryButtonText: 'Confirm & Submit',
      // user starts on step 1 in edit mode
      step: subscriptionTypeSection,
    };
  },

  computed: {
    ...mapGetters(paymentModuleName, {
      areRequiredPaymentFieldsSet: 'areRequiredPaymentFieldsSet',
      paymentItem: 'item',
    }),

    ...mapGetters(pricingModule, {
      isAnnual: 'getIsAnnual',
      userCount: 'getUserCount',
    }),

    hasReachedFinalReview() {
      return this.farthestStepReached + 1 > this.sections.length;
    },

    isFinalReviewStep() {
      return this.step === finalReview;
    },

    // Payment method needs to be filled out to generate a new payment token if a new address is
    // addded, but doesn't need to be filled out if nothing changing on existing card
    isPaymentMethodFilledOut() {
      if (this.isBillingEdited) {
        return !!get(this.paymentItem, 'card.paymentToken', false);
      }
      return this.areRequiredPaymentFieldsSet;
    },

    isPaymentMethodMinimized() {
      return this.step === billingInformationSection || !this.areRequiredPaymentFieldsSet;
    },

    sectionIds() {
      return this.sections.map(sect => sect.id);
    },

    sections() {
      const sections = [
        {
          component: 'SubscriptionTypeSection',
          handleContinue: this.handleContinue,
          id: subscriptionTypeSection,
          isFilledOut: this.farthestStepReached > 0,
          props: {
            section: 'Step One -- Subscription Type',
          },
        }, {
          component: 'BillingInformationSection',
          handleContinue: this.handleBillingContinue,
          id: billingInformationSection,
          isFilledOut: this.areRequiredPaymentFieldsSet,
          props: {
            id: this.id,
            isMinimized: !this.areRequiredPaymentFieldsSet,
            section: `Step ${this.showAddOns ? 'Three' : 'Two'} -- Billing Information`,
          },
        }, {
          component: 'PaymentMethodSection',
          handleContinue: this.handlePaymentContinue,
          id: paymentMethodSection,
          isFilledOut: this.isPaymentMethodFilledOut,
          props: {
            isMinimized: this.isPaymentMethodMinimized,
            section: `Step ${this.showAddOns ? 'Four' : 'Three'} -- Payment Method`,
          },
        },
      ];

      if (this.showAddOns) {
        sections.splice(1, 0, {
          component: 'AddOnFeaturesSection',
          handleContinue: this.handleContinue,
          id: addOnFeaturesSection,
          isFilledOut: this.farthestStepReached > 1,
          props: {
            section: 'Step Two -- Select Add-On Features',
          },
        });
      }

      return sections;
    },

    showAddOns() {
      return this.$hasFeatureFlag(this.$featureFlags.purchaseAddOnsInProduct)
        && this.productCode !== getProductCodeByType(SubscriptionTypes.alaCarte);
    },
  },

  created() {
    loadVuexModule({
      store: this.$store,
      module: PaymentModel,
      moduleName: paymentModuleName,
    });
  },

  async mounted() {
    this.resetItem();
    this.getPaymentInfo();
  },

  methods: {
    ...mapActions(settingsModule, [
      'fetchSettings',
    ]),
    ...mapActions(paymentModuleName, [
      'fetchItem',
      'resetItem',
    ]),
    ...mapActions(orgModel, [
      'subscribeToPlan',
    ]),
    ...mapActions(planModule, ['fetchPlan']),

    buildSubscriptionRequest() {
      const payload = {
        components: this.components,
        isAnnualContract: this.isAnnual,
        orgId: this.id,
        productCode: this.productCode,
      };

      if (this.isAnnual) {
        if (this.userCount > 10) {
          payload.userCount = this.userCount;
        } else {
          throw new Error('Must have committed to more than 10 users to initiate an annual contract.');
        }
      }

      return payload;
    },

    async getPaymentInfo() {
      try {
        await this.fetchItem(this.id);
      } catch (err) {
        this.$emit('error', 'Error while fetching payment information.');
      } finally {
        this.isLoading = false;
      }
    },

    handleBillingContinue(isBillingEdited) {
      this.isBillingEdited = isBillingEdited;
      if (isBillingEdited) {
        // Always force user to update payment method when billing address edited bc it's
        // only persisted to BE at that point
        this.step = paymentMethodSection;
        this.farthestStepReached = this.sectionIds.indexOf(paymentMethodSection);
      } else {
        this.handleContinue(billingInformationSection);
      }
    },

    /**
     * Go to the next checkout section, skipping sections that are already filled out.
     */
    handleContinue(currentStep) {
      if (this.hasReachedFinalReview) {
        this.step = finalReview;
        return;
      }

      const currentStepIndex = this.sectionIds.indexOf(currentStep);
      if (currentStepIndex === this.farthestStepReached) {
        this.farthestStepReached += 1;
      }
      const nextStep = this.sections.at(currentStepIndex + 1);
      if (!nextStep) { // No more sections = completed all steps
        this.step = finalReview;
        return;
      }

      if (nextStep.isFilledOut) {
        // Skip sections already filled out
        this.handleContinue(nextStep.id);
      } else {
        this.step = nextStep.id;
      }
    },

    handleEdit(currentStep) {
      this.step = currentStep;
    },

    handleError(message) {
      this.$emit('error', message);
    },

    handlePaymentContinue() {
      this.isBillingEdited = false;
      this.handleContinue(paymentMethodSection);
    },

    async handleSubmit() {
      this.primaryButtonIsLoading = true;

      try {
        const payload = this.buildSubscriptionRequest();
        await this.subscribeToPlan(payload);

        this.$options.EventHelper.triggerButtonClicked({
          [description]: 'confirm & submit clicked on Payment Info Modal',
          [location]: this.location,
          [section]: 'Payment Info Modal footer',
          [textEventProp]: 'Confirm & Submit',
        });

        this.$emit('success');

        this.$options.EventHelper.triggerPaymentInfoSubmitted();

        // Refresh 'isCustomer' and 'hasCreditCard' to keep paywall updated
        await this.fetchSettings();
        // Refresh plan to remove Pricing button from mainHeader
        await this.fetchPlan();
      } catch (err) {
        this.handleError('Unable to subscribe to plan.');
      } finally {
        this.primaryButtonIsLoading = false;
      }
    },
  },
};
</script>

<style module scoped>
.loading {
  text-align: center;
  width: 100%;
}

.primaryButton {
  margin-top: var(--jc-spacer);
}

.buttonContainer {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.contentContainer {
  height: 100%;
}

.contentContainer,
.sectionContainer {
  display: flex;
  flex-direction: column;
  gap: var(--jc-spacer);
}

.horizontalLine {
  width: 100%;
}
</style>
